/**
 * The account persona's disclosure (account-persona 03 §1, 08): the persona is an AI simulation of a public account,
 * not the account, not affiliated with it, not endorsed by it — said at the top of its instructions and whenever it
 * is asked who it is (EU AI Act Art. 50(1): the person is told they are dealing with an AI system; the owner's Q1:
 * a named persona with a persistent label). One constant, so every surface says the same words.
 */
import { z } from 'zod';

/** The label every surface shows beside the persona's name. */
export const ACCOUNT_PERSONA_LABEL = 'AI simulation';

/**
 * An account's name or handle as one inert line. It is the platform's text (a channel title, a display name) and it
 * reaches the persona's standing instructions outside the data fence, so it keeps no line break, no section or fence
 * brackets and no markup characters — any of them could open a section of its own. Whitespace collapses; capped.
 */
export function personaInlineText(value: string, max = 80): string {
  return (
    value
      // Compatibility forms first: a full-width `\uff3b` or `\uff5b` becomes the bracket the next step removes.
      .normalize('NFKC')
      .replace(/[\p{Cc}\p{Cf}\u2028\u2029]/gu, ' ')
      .replace(/[[\]{}<>`"\\|]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, max)
      .trim()
  );
}

/** The persona's display name: the account's name, labelled. */
export function accountPersonaName(displayName: string): string {
  return `${personaInlineText(displayName)} (${ACCOUNT_PERSONA_LABEL})`;
}

/** The disclosure sentence for one account. */
export function accountPersonaDisclosure(input: { handle: string; platform: string }): string {
  const handle = atHandle(input.handle);
  return (
    `You are an AI simulation built from the public posts of ${handle} on ${input.platform}. You are not ${handle}, ` +
    `not affiliated with ${handle}, and not endorsed by ${handle}. Say so when asked who you are.`
  );
}

function atHandle(value: string): string {
  const inline = personaInlineText(value);
  return inline.startsWith('@') ? inline : `@${inline}`;
}

/**
 * What every output of an account persona carries (account-persona 04): a conversation message (its footer), a card
 * (its badge), a workflow part (its header), a Worker SDK result and an MCP tool's output. `evaluated` is false while
 * the version serves before its evaluation (the v1 bootstrap, 00 2026-10-13): the person is told so on every output.
 */
export const AccountPersonaDisclosureSchema = z.object({
  label: z.literal(ACCOUNT_PERSONA_LABEL),
  /** "AI simulation of @handle — not @handle, not affiliated with or endorsed by @handle. Research only, not advice." */
  text: z.string().min(1).max(400),
  handle: z.string().min(1).max(81),
  platform: z.string().min(1).max(40),
  /** False while the version has not passed its evaluation. */
  evaluated: z.boolean(),
});
export type AccountPersonaDisclosure = z.infer<typeof AccountPersonaDisclosureSchema>;

/** What the person reads beside the persona's every output. */
export function accountPersonaDisclosureRecord(input: {
  handle: string;
  platform: string;
  evaluated: boolean;
}): AccountPersonaDisclosure {
  const handle = atHandle(input.handle);
  const platform = personaInlineText(input.platform, 40) || 'the platform';
  return {
    label: ACCOUNT_PERSONA_LABEL,
    text:
      `AI simulation of ${handle} on ${platform} — not ${handle}, not affiliated with or endorsed by ${handle}. ` +
      `Research only, not advice.${input.evaluated ? '' : ' This version has not been evaluated yet.'}`,
    handle,
    platform,
    evaluated: input.evaluated,
  };
}

/**
 * The notice a conversation with an account persona opens with, before its first answer (EU AI Act Art. 50(1): at
 * or before the first interaction, in the conversation itself — not a line in the terms).
 */
export function accountPersonaNotice(disclosure: AccountPersonaDisclosure): string {
  return (
    `You are talking to an AI simulation of ${disclosure.handle}, built from ${disclosure.handle}'s public posts on ` +
    `${disclosure.platform}. It is not ${disclosure.handle} and is not affiliated with or endorsed by ` +
    `${disclosure.handle}. Its answers cite what ${disclosure.handle} said and are research only, not advice.` +
    (disclosure.evaluated ? '' : ' This version has not been evaluated yet.')
  );
}
